import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Download, Mail, ArrowRight, Database, Shield, Code, Terminal } from 'lucide-react';

const APIs = () => {
  const navigate = useNavigate();

  const apiServices = [
    {
      icon: Search,
      title: 'Consulta RUC/DNI',
      description: 'Validación instantánea de contribuyentes con datos oficiales SUNAT',
      endpoint: 'GET /v1/ruc/{numero}',
      features: ['JSON Response', 'Cache inteligente', 'Validación en tiempo real'],
      color: 'from-blue-500 to-blue-600',
      docsSection: 'ruc-dni'
    },
    {
      icon: FileText,
      title: 'Envío de Documentos',
      description: 'Emisión masiva de facturas, boletas y guías de remisión',
      endpoint: 'POST /v1/documents',
      features: ['SOAP & REST', 'Validación automática', 'Tracking en tiempo real'],
      color: 'from-green-500 to-green-600',
      docsSection: 'envio-documentos'
    },
    {
      icon: FileText,
      title: 'Notas & Comunicaciones',
      description: 'Gestión de notas de crédito/débito y comunicaciones de baja',
      endpoint: 'POST /v1/notes',
      features: ['Anulaciones seguras', 'CDR inmediato', 'Historial completo'],
      color: 'from-purple-500 to-purple-600',
      docsSection: 'notas-comunicaciones'
    },
    {
      icon: Download,
      title: 'Descarga de Archivos',
      description: 'Recuperación de XML, PDF y CDR para auditorías',
      endpoint: 'GET /v1/files/{ticket}',
      features: ['Formatos múltiples', 'Descarga masiva', 'Compresión automática'],
      color: 'from-orange-500 to-orange-600',
      docsSection: 'descarga-archivos'
    },
    {
      icon: Mail,
      title: 'Buzón Electrónico',
      description: 'Monitoreo automatizado de comunicaciones SUNAT',
      endpoint: 'GET /v1/inbox',
      features: ['Webhooks', 'Notificaciones push', 'Filtros avanzados'],
      color: 'from-cyan-500 to-cyan-600',
      docsSection: 'buzon-electronico'
    },
    {
      icon: Database,
      title: 'Tipo de Cambio',
      description: 'Consulta diaria de tasas de cambio oficiales',
      endpoint: 'GET /v1/exchange-rate',
      features: ['Actualización diaria', 'Histórico completo', 'Múltiples monedas'],
      color: 'from-teal-500 to-teal-600',
      docsSection: 'tipo-cambio'
    }
  ];

  const handleDocsClick = (section = '') => {
    navigate('/documentacion', {
      state: {
        scrollTo: section,
        timestamp: Date.now() // Para forzar re-render si ya está en la misma página
      }
    });
  };

  const handleExamplesClick = (service) => {
    navigate('/documentacion', {
      state: {
        scrollTo: service.docsSection,
        view: 'examples',
        timestamp: Date.now()
      }
    });
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden border-t border-gray-200">
      {/* Efectos de fondo consistentes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0 bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>

        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-500/20 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight / 2 + 200,
            }}
            animate={{
              scale: [1, 1.8, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 4,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Encabezado */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-50px" }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 rounded-lg px-4 py-2 mb-6"
          >
            <Terminal className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-mono text-blue-700">REST API • SOAP • WEBHOOKS</span>
          </motion.div>

          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            APIs{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
              Especializadas
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Soluciones técnicas completas para integración con SUNAT. Documentación clara,
            SDKs disponibles y soporte para desarrolladores.
          </p>
        </motion.div>

        {/* Grid de APIs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {apiServices.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-300"
                whileHover={{ y: -4 }}
              >
                {/* Header con icono */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${service.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <motion.button
                    whileHover={{ x: 2 }}
                    onClick={() => handleDocsClick(service.docsSection)}
                    className="text-blue-600 text-xs font-mono bg-blue-50 px-2 py-1 rounded border border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors"
                  >
                    Docs →
                  </motion.button>
                </div>

                {/* Contenido */}
                <h3 className="font-bold text-gray-900 text-lg mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{service.description}</p>

                {/* Endpoint */}
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4 font-mono text-xs text-gray-700 group-hover:border-gray-300 transition-colors">
                  {service.endpoint}
                </div>

                {/* Features */}
                <div className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      <span className="text-gray-600 text-xs">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Footer con CTA */}
                <motion.div
                  className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                >
                  <span className="text-gray-500 text-xs font-medium">HTTP • JSON • SSL</span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleExamplesClick(service)}
                    className="text-blue-600 text-xs font-medium hover:text-blue-700 transition-colors inline-flex items-center space-x-1"
                  >
                    <span>Ver ejemplos</span>
                    <ArrowRight className="w-3 h-3" />
                  </motion.button>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Sección de características técnicas */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-20 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-8"
        >
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <Code className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-900 mb-2">SDKs Disponibles</h4>
              <p className="text-gray-600 text-sm">Python, JavaScript, Java, PHP, C#</p>
            </div>
            <div className="text-center">
              <Shield className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-900 mb-2">Seguridad Enterprise</h4>
              <p className="text-gray-600 text-sm">TLS 1.3, OAuth 2.0, Rate Limiting</p>
            </div>
            <div className="text-center">
              <Database className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-900 mb-2">Alta Disponibilidad</h4>
              <p className="text-gray-600 text-sm">99.9% uptime, réplica en tiempo real</p>
            </div>
          </div>
        </motion.div>

        {/* CTA Final */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="bg-white border border-gray-200 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-2">¿Necesitas una API específica?</h3>
            <p className="text-gray-600 mb-6">Contamos con endpoints especializados para cada necesidad tributaria</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleDocsClick()}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium text-sm border border-blue-700 hover:bg-blue-700 transition-colors"
              >
                Ver Documentación Completa
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/contacto')}
                className="border border-gray-300 text-gray-700 bg-white px-6 py-3 rounded-lg font-medium text-sm hover:border-gray-400 transition-colors"
              >
                Contactar Soporte Técnico
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default APIs;